import { useContext, useEffect, useState } from "react";
import { PitchContext } from "../context/PitchContex";
import axios from "axios";
import { toast } from "react-toastify";
import Spinner from "../components/Spinner";
import AccessDenied from "../components/AccessDenied";

function EditProfile() {
  const [image, setImage] = useState(null);
  const [url, setUrl] = useState("");
  const {
    user,
    backendURL,
    token,
    pending,
    setPending,
    setIsErr,
    setShowAlert,
    navigate,
    setAlertMessage,
  } = useContext(PitchContext);

  const [formData, setFormData] = useState(structuredClone(user));
  const [previewUrl, setPreviewUrl] = useState(formData.profilePicture);
  const [newData, setNewData] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [user]);

  const handleImageChange = (e) => {
    const selectedImage = e.target.files[0];
    if (selectedImage) {
      setImage(selectedImage);
      setPreviewUrl(URL.createObjectURL(selectedImage));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setNewData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPending(true);

    try {
      const formData = new FormData();

      // Append all key-value pairs from newData
      Object.entries(newData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Append the image if it exists
      if (image) {
        formData.append("image", image); // Matches backend's upload.single("image")
      }
      formData.append("userID", user._id);
      if (user.playerProfile.isCreated) {
        formData.append("playerID", user.playerProfile.profile[0]._id);
      } else {
        formData.append("playerID", "0");
      }

      const response = await axios.post(`${backendURL}user/update`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setPending(false);
        setIsErr(false);
        setAlertMessage(response.data.message);
        setShowAlert(true);
        navigate("/profil");
      } else {
        setPending(false);
        setIsErr(true);
        setAlertMessage(response.data.message);
        setShowAlert(true);
      }
    } catch (err) {
      setPending(false);
      console.error("Submit error:", err);
      setAlertMessage(err.response?.data?.error || "An error occurred");
      setIsErr(true);
      setShowAlert(true);
    }
  };

  if (!token) {
    return <AccessDenied />;
  }

  return pending ? (
    <Spinner />
  ) : (
    <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
          Edit Your Profile
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Update your personal information and profile picture below.
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Picture */}
          <div className="flex flex-col items-center">
            <label htmlFor="singleImage" className="cursor-pointer group mb-4">
              <div className="w-32 h-32 bg-gray-100 rounded-full overflow-hidden flex items-center justify-center border-2 border-dashed border-gray-300 group-hover:border-[#6ca87e] transition-all duration-200">
                <img
                  className="w-full h-full object-cover"
                  src={
                    previewUrl ||
                    "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541"
                  }
                  alt="Profile Preview"
                />
              </div>
              <input
                onChange={handleImageChange}
                type="file"
                id="singleImage"
                accept="image/*"
                hidden
              />
            </label>
            <span className="text-gray-600 text-sm">
              Click to upload a new profile picture
            </span>
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Full Name
            </label>
            <input
              type="text"
              name="fullname"
              value={formData.fullname}
              onChange={handleChange}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#6ca87e]/50 focus:border-[#6ca87e] transition-all"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#6ca87e]/50 focus:border-[#6ca87e] transition-all"
              required
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#6ca87e]/50 focus:border-[#6ca87e] transition-all"
            />
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Address</h3>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#6ca87e]/50 focus:border-[#6ca87e] transition-all"
                required
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="submit"
              className={`w-full bg-[#6ca87e] text-white py-2.5 rounded-lg font-semibold hover:bg-[#5d8f6c] transition-colors duration-200 cursor-pointer ${
                pending ? "opacity-70 cursor-not-allowed" : ""
              }`}
              disabled={pending}
            >
              {pending ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/profil")}
              className="w-full bg-gray-200 text-gray-800 py-2.5 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200 cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default EditProfile;
